import React, { useCallback, useMemo, useState } from "react";
import { Button, Modal, Tag } from "antd";
import { useTranslation } from "react-i18next";
import Table, { ColumnsType } from "antd/lib/table";
import { parseISO } from "date-fns";
import { HistoryOutlined } from "@ant-design/icons";
import { string } from "yup";
import { IUser } from "../../Users";
import UserAvatar from "../../../components/UserAvatar";
import { IDetailedProject } from "..";
import api from "../../../services/api";
import { useAuth } from "../../../hooks/auth";
import UserRoles from "../../../enums/UserRoles";

interface IValueHistoryItem {
  id: string;
  old_value: any;
  new_value: any;
  executor: IUser;
  created_at: Date;
}

interface IFieldValueHistoryModalProps {
  project: IDetailedProject;
  fieldName: string;
  valueRenderFunction?: (value: string) => string;
}

const FieldValueHistoryModal: React.FC<IFieldValueHistoryModalProps> = ({
  project,
  fieldName,
  valueRenderFunction,
}) => {
  const { t } = useTranslation(["projects"]);
  const [visible, setVisible] = useState<boolean>(false);
  const [history, setHistory] = useState<IValueHistoryItem[]>([]);
  const { user } = useAuth();

  const isLoggedUserManager = useMemo(
    () =>
      user.role === UserRoles.Manager || user.role === UserRoles.Administrative,
    [user.role],
  );

  const handleFindHistory = useCallback(() => {
    api
      .get(`/projects/${project.id}/field_change_history`, {
        params: { field_name: fieldName },
      })
      .then(response => {
        setHistory(response.data);
      });
  }, [project.id, fieldName]);

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  const actionsHistoryColumns: ColumnsType<IValueHistoryItem> = [
    {
      title: t("date"),
      dataIndex: "created_at",
      render: date =>
        date &&
        `${parseISO(date).toLocaleDateString("pt-BR")} às ${parseISO(
          date,
        ).toLocaleTimeString("pt-BR")}`,
    },
    {
      title: t("user"),
      dataIndex: "executor",
      render: (creator: IUser) => {
        if (creator) {
          return (
            <UserAvatar
              name={creator.name}
              url={creator.avatar_url}
              size={36}
            />
          );
        }
        return <></>;
      },
    },
    {
      title: t("old_value"),
      dataIndex: "old_value",
      render: valueRenderFunction,
    },
    {
      title: t("new_value"),
      dataIndex: "new_value",
      render: valueRenderFunction,
    },
  ];

  return isLoggedUserManager ? (
    <>
      <Modal
        title={`${t("change_history")} - ${t(fieldName)}`}
        visible={visible}
        destroyOnClose
        width={800}
        onCancel={() => handleClose()}
        getContainer={false}
        footer={[
          <Button type="primary" key="ok" onClick={() => handleClose()}>
            Ok
          </Button>,
        ]}
      >
        <Table
          dataSource={history}
          rowKey={record => record.id}
          columns={actionsHistoryColumns}
          pagination={false}
          size="small"
        />
      </Modal>
      <Button
        type="link"
        shape="circle"
        icon={<HistoryOutlined />}
        onClick={() => {
          handleFindHistory();
          setVisible(oldVisible => !oldVisible);
        }}
      />
    </>
  ) : (
    <></>
  );
};

export default FieldValueHistoryModal;
